import { rect, px, mPx } from '../../../pixel/canvas';
import type { Part, Anchor, Palette } from '../../types';

// Anchor = top of head. Cranium attachments paint UPWARD from the anchor.
// Most extend in y-range a.y - 6 .. a.y.

const drawThirdEyeOpen = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  px(ctx, a.x, a.y - 2, p.accent);
  px(ctx, a.x, a.y - 1, p.accentLight);
};

const drawThirdEyeClosed = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  px(ctx, a.x, a.y - 2, p.metalDark);
};

const drawCrystalTopLit = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Diamond shape, 3 wide, 4 tall, centered on a.x, top at a.y - 4.
  // Top facet highlight.
  px(ctx, a.x, a.y - 4, p.glassLight); // top point (lit)
  px(ctx, a.x - 1, a.y - 3, p.glass);  // upper-left facet
  px(ctx, a.x, a.y - 3, p.glass);      // upper-mid
  px(ctx, a.x + 1, a.y - 3, p.glass);  // upper-right facet
  px(ctx, a.x - 1, a.y - 2, p.glass);  // mid-left
  px(ctx, a.x, a.y - 2, p.glass);      // mid-center
  px(ctx, a.x + 1, a.y - 2, p.glass);  // mid-right
  px(ctx, a.x, a.y - 1, p.glass);      // bottom point
};

const drawCrystalBottomLit = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Same shape; highlight shifts to the bottom point.
  px(ctx, a.x, a.y - 4, p.glass);      // top point
  px(ctx, a.x - 1, a.y - 3, p.glass);
  px(ctx, a.x, a.y - 3, p.glass);
  px(ctx, a.x + 1, a.y - 3, p.glass);
  px(ctx, a.x - 1, a.y - 2, p.glass);
  px(ctx, a.x, a.y - 2, p.glass);
  px(ctx, a.x + 1, a.y - 2, p.glass);
  px(ctx, a.x, a.y - 1, p.glassLight); // bottom point (lit)
};

export const cranium: Record<string, Part> = {
  horns: {
    id: 'horns',
    category: 'cranium',
    name: 'Horns',
    requires: 'cranium',
    weights: { ferocity: 12, resilience: 5 },
    draw: (ctx, a, p) => {
      // Left horn: outer dark body, inner highlight.
      rect(ctx, a.x - 3, a.y - 5, 1, 4, p.metalDark);
      rect(ctx, a.x - 2, a.y - 3, 1, 2, p.metal);
      // Right horn: mirrored.
      rect(ctx, a.x + 2, a.y - 5, 1, 4, p.metalDark);
      rect(ctx, a.x + 1, a.y - 3, 1, 2, p.metal);
      // Tip highlights.
      mPx(ctx, 2, a.y - 5, p.metalLight);
    },
  },
  antlers: {
    id: 'antlers',
    category: 'cranium',
    name: 'Antlers',
    requires: 'cranium',
    weights: { arcane: 6, agility: 4, ferocity: 4 },
    draw: (ctx, a, p) => {
      // Main beams going up from (a.x ± 1, a.y).
      rect(ctx, a.x - 2, a.y - 6, 1, 6, p.detail);
      rect(ctx, a.x + 1, a.y - 6, 1, 6, p.detail);
      // Shadow side (inner edge).
      px(ctx, a.x - 2, a.y - 1, p.detailDark);
      px(ctx, a.x + 1, a.y - 1, p.detailDark);
      // Left antler forks.
      px(ctx, a.x - 3, a.y - 4, p.detail); // lower outer fork
      px(ctx, a.x - 4, a.y - 4, p.detailDark);
      px(ctx, a.x - 1, a.y - 5, p.detail); // upper inner fork
      px(ctx, a.x - 3, a.y - 6, p.detail); // upper outer fork tip
      // Right antler forks (mirrored).
      px(ctx, a.x + 2, a.y - 4, p.detail);
      px(ctx, a.x + 3, a.y - 4, p.detailDark);
      px(ctx, a.x, a.y - 5, p.detail);
      px(ctx, a.x + 2, a.y - 6, p.detail);
    },
  },
  third_eye: {
    id: 'third_eye',
    category: 'cranium',
    name: 'Third Eye',
    requires: 'cranium',
    weights: { arcane: 18, stealth: 6 },
    draw: drawThirdEyeOpen,
    frames: [
      { draw: drawThirdEyeOpen },
      { draw: drawThirdEyeOpen },
      { draw: drawThirdEyeOpen },
      { draw: drawThirdEyeClosed },
    ],
  },
  crystal: {
    id: 'crystal',
    category: 'cranium',
    name: 'Crystal',
    requires: 'cranium',
    weights: { arcane: 14, resilience: 4 },
    draw: drawCrystalTopLit,
    frames: [
      { draw: drawCrystalTopLit },
      { draw: drawCrystalBottomLit },
    ],
  },
};
